import { useMutation } from '@apollo/client'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import {
  Button,
  ClickAwayListener,
  FormControl,
  Grid2,
  IconButton,
  Input,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import moment from 'moment'
import React from 'react'
import { removeTypename } from '../../../common/utils'
import { i18n } from '../../index/index'
import { removeExpressCheckoutCategoryPeriod, upsertExpressCheckoutPeriodSchedule } from '../expressCheckoutQueries'
import { Category, OpeningHoursPeriod, RestaurantOpeningHours } from '../types'
import { WEEKDAYS } from '../util'
import MultipleTimeSelectRow from './MultipleTimeSelectRow'
import TimeSelectRow from './TimeSelectRow'

interface OpeningHoursFormProps {
  stationId: string
  defaultOpeningHours: RestaurantOpeningHours
  category: Category
  onCompleted: () => void
  defaultStartDate?: moment.Moment
  defaultEndDate?: moment.Moment
  periodId?: string
  defaultPeriodName?: string
  existingPeriods?: OpeningHoursPeriod[]
}

function getOverlappingPeriodErrorMessage(overlappingPeriods: OpeningHoursPeriod[]): string {
  const periodNames = overlappingPeriods
    .map(
      (period) =>
        `${period.periodName}: ${moment(period.startDate).format('DD.MM.YYYY')}-${moment(period.endDate).format('DD.MM.YYYY')}`,
    )
    .join(', ')
  return `${i18n.t('restaurantMenu.errorMessages.overlappingCategoryPeriod')} (${periodNames})`
}

function getEndDateOfPeriod(period?: OpeningHoursPeriod): moment.Moment | null {
  if (!period || !period.endDate) {
    return null
  }
  return moment(period.endDate, 'YYYY-MM-DD')
}

export function OpeningHoursPeriodForm({
  defaultOpeningHours,
  defaultStartDate,
  defaultEndDate,
  category,
  periodId,
  defaultPeriodName,
  stationId,
  onCompleted,
  existingPeriods = [],
}: OpeningHoursFormProps) {
  const newestExistingPeriod = existingPeriods.slice().sort((period1, period2) => {
    const endDate1 = moment(period1.endDate, 'YYYY-MM-DD')
    const endDate2 = moment(period2.endDate, 'YYYY-MM-DD')
    return endDate2.diff(endDate1)
  })[0]

  const suggestedStartDate = defaultStartDate ?? getEndDateOfPeriod(newestExistingPeriod)?.add(1, 'day') ?? moment()
  const suggestedEndDate = defaultEndDate ?? suggestedStartDate.clone().add(1, 'month')

  const [startDate, setStartDate] = React.useState<moment.Moment | null>(suggestedStartDate)
  const [endDate, setEndDate] = React.useState<moment.Moment | null>(suggestedEndDate)
  const [periodName, setPeriodName] = React.useState<string>(defaultPeriodName ?? '')
  const [openingHours, setOpeningHours] = React.useState<RestaurantOpeningHours>(removeTypename(defaultOpeningHours))
  const [mutationFunction, mutationResult] = useMutation(upsertExpressCheckoutPeriodSchedule, {
    onCompleted,
  })

  const [removeMutationFunction, removeMutationResult] = useMutation(removeExpressCheckoutCategoryPeriod, {
    onCompleted,
  })

  const [popperOpen, setPopperOpen] = React.useState<{ [key: string]: HTMLElement | null }>({})

  function selectTime(weekDay: keyof RestaurantOpeningHours, field: 'opens' | 'closes', time: string | null) {
    if (!time) {
      return
    }
    setOpeningHours({
      ...openingHours,
      [weekDay]: {
        ...openingHours[weekDay],
        [field]: time,
      },
    })
  }

  function setSchedules(weekDay: keyof RestaurantOpeningHours, schedules: { opens: string; closes: string }[] | null) {
    setOpeningHours({
      ...openingHours,
      [weekDay]: {
        ...openingHours[weekDay],
        schedules,
      },
    })
  }

  function setTimes(weekDay: keyof RestaurantOpeningHours, opens: string, closes: string) {
    setOpeningHours({
      ...openingHours,
      [weekDay]: {
        opens,
        closes,
        schedules: null,
      },
    })
  }

  function toggleSchedules(weekDay: keyof RestaurantOpeningHours) {
    const hasSchedules = !!openingHours[weekDay].schedules
    if (hasSchedules) {
      setOpeningHours({
        ...openingHours,
        [weekDay]: {
          opens: openingHours[weekDay].opens ?? '00:00',
          closes: openingHours[weekDay].closes ?? '00:00',
          schedules: null,
        },
      })
    } else {
      setOpeningHours({
        ...openingHours,
        [weekDay]: {
          opens: null,
          closes: null,
          schedules: [
            { opens: openingHours[weekDay].opens ?? '00:00', closes: openingHours[weekDay].closes ?? '00:00' },
          ],
        },
      })
    }
  }

  function getOverlappingPeriods(): OpeningHoursPeriod[] {
    if (!startDate || !endDate) {
      return []
    }
    return existingPeriods.filter((period) => {
      if (periodId && period.periodId === periodId) {
        return false
      }
      const periodStart = moment(period.startDate, 'YYYY-MM-DD')
      const periodEnd = moment(period.endDate, 'YYYY-MM-DD')
      return startDate.isSameOrBefore(periodEnd) && endDate.isSameOrAfter(periodStart)
    })
  }

  function isValid() {
    return (
      startDate &&
      endDate &&
      Object.values(openingHours).every(
        ({ opens, closes, schedules }) => (opens && closes) || (schedules && schedules.length > 0),
      ) &&
      startDate.isSameOrBefore(endDate) &&
      !getOverlappingPeriods().length
    )
  }

  function showErrorMessages() {
    return (
      periodName &&
      startDate &&
      endDate &&
      Object.values(openingHours).every(
        ({ opens, closes, schedules }) => (opens && closes) || (schedules && schedules.length > 0),
      ) &&
      !isValid()
    )
  }

  function getErrorMessages(): string[] {
    const messages: string[] = []
    const overlappingPeriods = getOverlappingPeriods()
    if (overlappingPeriods.length) {
      messages.push(getOverlappingPeriodErrorMessage(overlappingPeriods))
    }
    if (startDate && endDate && endDate.isBefore(startDate)) {
      messages.push(i18n.t('restaurantMenu.errorMessages.endDateBeforeStartDate'))
    }
    return messages
  }

  function submit() {
    if (!isValid()) {
      return
    }
    const name = !periodName
      ? `${category.name} ${startDate?.format('D.M.YYYY')}-${endDate?.format('D.M.YYYY')}`
      : periodName
    mutationFunction({
      variables: {
        stationId: decodeURIComponent(stationId),
        period: {
          periodId: periodId,
          startDate: startDate?.format('YYYY-MM-DD'),
          endDate: endDate?.format('YYYY-MM-DD'),
          categoryName: category.name,
          categoryId: category.id,
          periodName: name,
          openingHours: {
            MON: {
              opens: openingHours.MON.opens,
              closes: openingHours.MON.closes,
              schedules: openingHours.MON.schedules,
            },
            TUE: {
              opens: openingHours.TUE.opens,
              closes: openingHours.TUE.closes,
              schedules: openingHours.TUE.schedules,
            },
            WED: {
              opens: openingHours.WED.opens,
              closes: openingHours.WED.closes,
              schedules: openingHours.WED.schedules,
            },
            THU: {
              opens: openingHours.THU.opens,
              closes: openingHours.THU.closes,
              schedules: openingHours.THU.schedules,
            },
            FRI: {
              opens: openingHours.FRI.opens,
              closes: openingHours.FRI.closes,
              schedules: openingHours.FRI.schedules,
            },
            SAT: {
              opens: openingHours.SAT.opens,
              closes: openingHours.SAT.closes,
              schedules: openingHours.SAT.schedules,
            },
            SUN: {
              opens: openingHours.SUN.opens,
              closes: openingHours.SUN.closes,
              schedules: openingHours.SUN.schedules,
            },
          },
        },
      },
    })
  }

  function remove() {
    if (!periodId) {
      return
    }
    removeMutationFunction({
      variables: {
        stationId: decodeURIComponent(stationId),
        periodId,
      },
    })
  }

  function togglePopper(day: string, anchorEl: HTMLElement | null) {
    if (popperOpen[day]) {
      setPopperOpen({ ...popperOpen, [day]: null })
      return
    }
    setPopperOpen({ ...popperOpen, [day]: anchorEl })
  }

  function copyOpeningHours(
    dayList: string[],
    opens?: string | null,
    closes?: string | null,
    schedules?: { opens: string; closes: string }[] | null,
  ) {
    const newOpeningHours = { ...openingHours }
    dayList.forEach((day) => {
      newOpeningHours[day as keyof RestaurantOpeningHours] = { opens, closes, schedules }
    })
    setOpeningHours(newOpeningHours)
  }

  return (
    <Paper sx={{ padding: 2 }}>
      <Stack spacing={2}>
        {periodId && (
          <Stack direction='row-reverse'>
            <FormControl>
              <Button variant='contained' color='error' onClick={remove}>
                {i18n.t('buttons.delete')}
              </Button>
            </FormControl>
          </Stack>
        )}
        <Stack direction='row' justifyContent='space-between'>
          <FormControl fullWidth margin='normal'>
            <Input
              type='text'
              placeholder={i18n.t('restaurantMenu.categoryPeriods.periodNamePlaceholder')}
              value={periodName}
              onChange={(e) => setPeriodName(e.target.value)}
            />
          </FormControl>
        </Stack>
        <Stack justifyContent='space-between' spacing={2}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <Stack direction='row' spacing={2} alignItems='center'>
              <DatePicker
                label={i18n.t('restaurantMenu.startDateLabel')}
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
              />
              <DatePicker
                label={i18n.t('restaurantMenu.endDateLabel')}
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
              />
            </Stack>
            <Stack>
              <Typography variant='h6' gutterBottom>
                {category.name}
              </Typography>
              <Stack spacing={2}>
                {Object.keys(removeTypename(defaultOpeningHours)).map((day) => {
                  const { schedules } = openingHours[day as keyof RestaurantOpeningHours]
                  const dayKey = WEEKDAYS[day as keyof RestaurantOpeningHours].translationKey
                  return (
                    <Grid2 direction='row' spacing={2} container key={dayKey}>
                      {schedules !== undefined && schedules !== null ? (
                        <MultipleTimeSelectRow
                          day={day as keyof RestaurantOpeningHours}
                          schedules={schedules}
                          opens={openingHours[day as keyof RestaurantOpeningHours].opens ?? '00:00'}
                          closes={openingHours[day as keyof RestaurantOpeningHours].closes ?? '00:00'}
                          setSchedules={(schedules) => setSchedules(day as keyof RestaurantOpeningHours, schedules)}
                        />
                      ) : (
                        <TimeSelectRow
                          day={day as keyof RestaurantOpeningHours}
                          openingHours={openingHours}
                          selectTime={selectTime}
                          togglePopper={togglePopper}
                        />
                      )}
                      <Grid2>
                        <IconButton size='small' onClick={(e) => togglePopper(day, e.currentTarget)}>
                          <MoreVertIcon />
                        </IconButton>
                      </Grid2>
                      <ClickAwayListener
                        mouseEvent='onMouseDown'
                        touchEvent='onTouchStart'
                        onClickAway={() => setPopperOpen({})}
                      >
                        <Popper open={!!popperOpen[day]} anchorEl={popperOpen[day]} placement='bottom-start'>
                          <Paper sx={{ padding: 2 }}>
                            <Stack spacing={2}>
                              <Button
                                variant='text'
                                onClick={() => {
                                  const { opens, closes, schedules } = openingHours[day as keyof RestaurantOpeningHours]
                                  copyOpeningHours(['MON', 'TUE', 'WED', 'THU', 'FRI'], opens, closes, schedules)
                                  togglePopper(day, null)
                                }}
                              >
                                {i18n.t('restaurantMenu.categoryPeriods.copyForWeekDays')}
                              </Button>
                              <Button
                                variant='text'
                                onClick={() => {
                                  const { opens, closes, schedules } = openingHours[day as keyof RestaurantOpeningHours]
                                  copyOpeningHours(['SAT', 'SUN'], opens, closes, schedules)
                                  togglePopper(day, null)
                                }}
                              >
                                {i18n.t('restaurantMenu.categoryPeriods.copyForWeekEnd')}
                              </Button>
                              <Button
                                variant='text'
                                onClick={() => {
                                  const { opens, closes, schedules } = openingHours[day as keyof RestaurantOpeningHours]
                                  copyOpeningHours(
                                    ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'],
                                    opens,
                                    closes,
                                    schedules,
                                  )
                                  togglePopper(day, null)
                                }}
                              >
                                {i18n.t('restaurantMenu.categoryPeriods.copyForWholeWeek')}
                              </Button>
                              {!schedules && (
                                <>
                                  <Button
                                    variant='text'
                                    onClick={() => {
                                      setTimes(day as keyof RestaurantOpeningHours, '00:00', '00:00')
                                    }}
                                  >
                                    {i18n.t('restaurantMenu.setClosedForDay')}
                                  </Button>
                                  <Button
                                    variant='text'
                                    onClick={() => {
                                      setTimes(day as keyof RestaurantOpeningHours, '00:00', '23:59')
                                    }}
                                  >
                                    {i18n.t('restaurantMenu.setAlwaysOpenForDay')}
                                  </Button>
                                </>
                              )}
                              <Button
                                variant='text'
                                onClick={() => {
                                  toggleSchedules(day as keyof RestaurantOpeningHours)
                                }}
                              >
                                {!schedules
                                  ? i18n.t('restaurantMenu.setMultipleSchedulesForDay')
                                  : i18n.t('restaurantMenu.unsetMultipleSchedulesForDay')}
                              </Button>
                            </Stack>
                          </Paper>
                        </Popper>
                      </ClickAwayListener>
                    </Grid2>
                  )
                })}
              </Stack>
              <Stack direction='row-reverse' spacing={2} sx={{ marginTop: '20px' }}>
                <Button variant='contained' onClick={submit} disabled={!isValid() || mutationResult.loading}>
                  {i18n.t('buttons.save')}
                </Button>
                {showErrorMessages() && (
                  <Stack spacing={1} direction='column'>
                    {getErrorMessages().map((message) => (
                      <Typography key={message} color='error' variant='body2'>
                        {message}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </LocalizationProvider>
        </Stack>
      </Stack>
    </Paper>
  )
}
